<template>
  <div class="unix-page">
    <h2 class="page-title">Unix 时间戳转换</h2>

    <!-- 复制成功提示 -->
    <div v-if="copied" class="copy-toast">已复制：{{ copied }}</div>

    <!-- 当前时间戳实时显示 -->
    <div class="now-section">
      <div class="now-card">
        <div class="now-label">当前 Unix 时间戳（秒）</div>
        <div class="now-value">
          <span class="ts-num">{{ currentTs }}</span>
          <button class="copy-btn" @click="copy(currentTs)" title="复制">📋</button>
        </div>
      </div>
      <div class="now-card">
        <div class="now-label">当前 Unix 时间戳（毫秒）</div>
        <div class="now-value">
          <span class="ts-num">{{ currentTsMs }}</span>
          <button class="copy-btn" @click="copy(currentTsMs)" title="复制">📋</button>
        </div>
      </div>
      <div class="now-card">
        <div class="now-label">当前北京时间</div>
        <div class="now-value">
          <span class="ts-num now-date">{{ currentDateTime }}</span>
        </div>
      </div>
    </div>

    <!-- 转换区域 -->
    <div class="convert-section">
      <!-- 时间戳 → 日期 -->
      <div class="convert-panel">
        <h3 class="panel-title">时间戳 → 日期时间</h3>
        <div class="input-group">
          <input
            v-model="tsInput"
            type="text"
            placeholder="输入 Unix 时间戳，如 1751529600"
            class="convert-input"
            @input="tsToDate"
          />
          <select v-model="tsUnit" class="unit-select" @change="tsToDate">
            <option value="second">秒</option>
            <option value="millisecond">毫秒</option>
          </select>
        </div>
        <div class="convert-result" v-if="tsResult">
          <div class="result-row">
            <span class="result-label">UTC 时间：</span>
            <span class="result-value">{{ tsResult.utc }}</span>
            <button class="copy-btn sm" @click="copy(tsResult.utc)">复制</button>
          </div>
          <div class="result-row">
            <span class="result-label">北京时间：</span>
            <span class="result-value">{{ tsResult.beijing }}</span>
            <button class="copy-btn sm" @click="copy(tsResult.beijing)">复制</button>
          </div>
          <div class="result-row">
            <span class="result-label">ISO 8601：</span>
            <span class="result-value">{{ tsResult.iso }}</span>
            <button class="copy-btn sm" @click="copy(tsResult.iso)">复制</button>
          </div>
          <div class="result-row">
            <span class="result-label">星期：</span>
            <span class="result-value">{{ tsResult.weekday }}</span>
          </div>
          <div class="result-row">
            <span class="result-label">年第{{ tsResult.weekNum }}周</span>
          </div>
        </div>
        <div class="quick-btns">
          <button class="quick-btn" @click="setNow">当前时间戳</button>
          <button class="quick-btn" @click="tsInput = '0'; tsToDate()">0（纪元起点）</button>
        </div>
      </div>

      <!-- 日期 → 时间戳 -->
      <div class="convert-panel">
        <h3 class="panel-title">日期时间 → 时间戳</h3>
        <div class="input-row">
          <input v-model="dateInput" type="date" class="date-input" @change="dateToTs" />
          <input v-model="timeInput" type="time" class="time-input" step="1" @change="dateToTs" />
        </div>
        <div class="convert-result" v-if="dateResult">
          <div class="result-row">
            <span class="result-label">时间戳（秒）：</span>
            <span class="result-value">{{ dateResult.seconds }}</span>
            <button class="copy-btn sm" @click="copy(dateResult.seconds)">复制</button>
          </div>
          <div class="result-row">
            <span class="result-label">时间戳（毫秒）：</span>
            <span class="result-value">{{ dateResult.milliseconds }}</span>
            <button class="copy-btn sm" @click="copy(dateResult.milliseconds)">复制</button>
          </div>
        </div>
        <div class="quick-btns">
          <button class="quick-btn" @click="setDateNow">当前时间</button>
        </div>
      </div>
    </div>

    <!-- 时间戳说明 -->
    <div class="info-section">
      <h3>关于 Unix 时间戳</h3>
      <p>Unix 时间戳是从 1970年1月1日（UTC/GMT 午夜）开始所经过的秒数，不考虑闰秒。</p>
      <p>Unix 时间戳（毫秒）是从同一时刻起经过的毫秒数，常用于 JavaScript 等编程语言。</p>
      <div class="code-example">
        <code>// JavaScript 获取当前时间戳
const ts = Math.floor(Date.now() / 1000);  // 秒
const tsMs = Date.now();                    // 毫秒</code>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UnixPage',
  data() {
    const now = new Date()
    return {
      currentTs: Math.floor(now.getTime() / 1000),
      currentTsMs: now.getTime(),
      currentDateTime: '',
      tsInput: '',
      tsUnit: 'second',
      tsResult: null,
      dateInput: '',
      timeInput: '',
      dateResult: null,
      timer: null,
      copied: ''
    }
  },
  mounted() {
    this.updateNow()
    this.timer = setInterval(this.updateNow, 200)
    this.setDateNow()
    this.dateToTs()
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    updateNow() {
      const now = new Date()
      this.currentTs = Math.floor(now.getTime() / 1000)
      this.currentTsMs = now.getTime()
      const pad = (n) => String(n).padStart(2, '0')
      this.currentDateTime = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
    },
    pad(n) { return String(n).padStart(2, '0') },
    tsToDate() {
      const val = this.tsInput.trim()
      if (!val) { this.tsResult = null; return }
      const num = parseInt(val, 10)
      if (isNaN(num)) { this.tsResult = null; return }
      let ms = num
      if (this.tsUnit === 'second') ms = num * 1000
      const d = new Date(ms)
      if (isNaN(d.getTime())) { this.tsResult = null; return }

      const days = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六']
      const bj = new Date(d.getTime() + 8 * 3600000)

      const getWeek = (date) => {
        const dc = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
        const dn = dc.getUTCDay() || 7
        dc.setUTCDate(dc.getUTCDate() + 4 - dn)
        const ys = new Date(Date.UTC(dc.getUTCFullYear(), 0, 1))
        return Math.ceil(((dc - ys) / 86400000 + 1) / 7)
      }

      this.tsResult = {
        utc: `${d.getUTCFullYear()}-${this.pad(d.getUTCMonth()+1)}-${this.pad(d.getUTCDate())} ${this.pad(d.getUTCHours())}:${this.pad(d.getUTCMinutes())}:${this.pad(d.getUTCSeconds())}`,
        beijing: `${bj.getUTCFullYear()}-${this.pad(bj.getUTCMonth()+1)}-${this.pad(bj.getUTCDate())} ${this.pad(bj.getUTCHours())}:${this.pad(bj.getUTCMinutes())}:${this.pad(bj.getUTCSeconds())}`,
        iso: d.toISOString(),
        weekday: days[d.getUTCDay()],
        weekNum: getWeek(d)
      }
    },
    dateToTs() {
      if (!this.dateInput) { this.dateResult = null; return }
      const dateStr = this.dateInput
      const timeStr = this.timeInput || '00:00:00'
      const d = new Date(dateStr + 'T' + timeStr + '+08:00')
      if (isNaN(d.getTime())) { this.dateResult = null; return }
      this.dateResult = {
        seconds: Math.floor(d.getTime() / 1000),
        milliseconds: d.getTime()
      }
    },
    setNow() {
      this.tsInput = String(Math.floor(Date.now() / 1000))
      this.tsUnit = 'second'
      this.tsToDate()
    },
    setDateNow() {
      const now = new Date()
      this.dateInput = `${now.getFullYear()}-${this.pad(now.getMonth()+1)}-${this.pad(now.getDate())}`
      this.timeInput = `${this.pad(now.getHours())}:${this.pad(now.getMinutes())}:${this.pad(now.getSeconds())}`
      this.dateToTs()
    },
    copy(text) {
      if (text == null) return
      const str = String(text)
      const showTip = () => {
        this.copied = str
        setTimeout(() => { this.copied = '' }, 1500)
      }
      const fallback = () => {
        const ta = document.createElement('textarea')
        ta.value = str
        ta.style.position = 'fixed'
        ta.style.left = '-9999px'
        document.body.appendChild(ta)
        ta.select()
        try {
          document.execCommand('copy')
          showTip()
        } catch (e) { /* ignore */ }
        document.body.removeChild(ta)
      }
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(str).then(showTip).catch(fallback)
      } else {
        fallback()
      }
    }
  }
}
</script>

<style scoped>
.unix-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px 16px 40px;
}

.page-title {
  text-align: center;
  font-size: 26px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 28px;
}

/* 当前时间戳 */
.now-section {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 30px;
}
.now-card {
  background: #1a1a2e;
  border-radius: 12px;
  padding: 18px 16px;
  text-align: center;
}
.now-label {
  font-size: 12px;
  color: #8899aa;
  margin-bottom: 8px;
}
.now-value {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.ts-num {
  font-size: 18px;
  font-weight: 600;
  color: #4ecdc4;
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-feature-settings: 'tnum';
  word-break: break-all;
}
.ts-num.now-date {
  font-size: 16px;
  color: #fff;
}
.copy-btn {
  background: rgba(255,255,255,0.15);
  border: none;
  color: #fff;
  cursor: pointer;
  border-radius: 4px;
  padding: 3px 6px;
  font-size: 13px;
  flex-shrink: 0;
}
.copy-btn:hover { background: rgba(255,255,255,0.3); }
.copy-btn.sm { padding: 2px 5px; font-size: 12px; background: #f0f0f0; color: #666; }
.copy-btn.sm:hover { background: #ddd; }

/* 转换区域 */
.convert-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
}
.convert-panel {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 8px rgba(0,0,0,0.06);
}
.panel-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 2px solid #1677ff;
}
.input-group {
  display: flex;
  gap: 8px;
}
.input-row {
  display: flex;
  gap: 8px;
}
.convert-input {
  flex: 1;
  padding: 10px 14px;
  font-size: 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  outline: none;
  transition: border 0.2s;
}
.convert-input:focus { border-color: #1677ff; }
.unit-select {
  padding: 10px 10px;
  font-size: 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  outline: none;
}
.date-input, .time-input {
  flex: 1;
  padding: 10px 12px;
  font-size: 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  outline: none;
}
.date-input:focus, .time-input:focus { border-color: #1677ff; }

.convert-result {
  margin-top: 14px;
  background: #f8f9fb;
  border-radius: 8px;
  padding: 12px 14px;
}
.result-row {
  display: flex;
  align-items: center;
  padding: 5px 0;
  font-size: 13px;
  gap: 6px;
}
.result-label {
  color: #888;
  white-space: nowrap;
  min-width: 85px;
}
.result-value {
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  color: #333;
  font-weight: 500;
  flex: 1;
}

.quick-btns {
  margin-top: 12px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.quick-btn {
  padding: 6px 12px;
  font-size: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}
.quick-btn:hover { background: #1677ff; color: #fff; border-color: #1677ff; }

/* 说明 */
.info-section {
  background: #fff;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 8px rgba(0,0,0,0.06);
}
.info-section h3 {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 10px;
}
.info-section p {
  font-size: 14px;
  color: #666;
  line-height: 1.8;
}
.code-example {
  margin-top: 12px;
  background: #1a1a2e;
  border-radius: 8px;
  padding: 14px 18px;
  overflow-x: auto;
}
.code-example code {
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  color: #4ecdc4;
  line-height: 1.7;
  white-space: pre;
}

.copy-toast {
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  background: #1a1a2e;
  color: #4ecdc4;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  animation: fadeInDown 0.3s ease;
}
@keyframes fadeInDown {
  from { opacity: 0; transform: translate(-50%, -10px); }
  to { opacity: 1; transform: translate(-50%, 0); }
}

@media (max-width: 768px) {
  .now-section { grid-template-columns: 1fr; }
  .convert-section { grid-template-columns: 1fr; }
  .ts-num { font-size: 15px; }
}
</style>
